/**
 * Details: everything recorded about an item, as a name and a value. Underneath, a detail is
 * either a tag (`area/honolulu/kaimuki`: shared values, general → specific, or a plain marker
 * like `favorite`) or a field (an item's own value that keeps its spelling: a phone number, an
 * address, a link, a cost). In the app they're one thing: one + adds either, and each shows on
 * the item's quick line (under its title) or in its grid (lower down), wherever its name is set
 * to show. What a value can do comes from the value itself: a phone number calls, a link opens,
 * a cost adds up, an address gets a map pin.
 */
import { fieldKeyFromLabel, formatFieldValue, hasValue } from './fields'
import { normalizeTag } from './doc'
import { isUnderTag, tagGroupOf, tagLabel } from './tags'
import type { FieldDef, FieldType, ItemSnapshot } from './types'
import type { CalcInfo } from './math'

export type DetailPlace = 'line' | 'grid'
export type ValueKind = 'phone' | 'link' | 'money' | 'date' | 'address' | 'text'

export interface Detail {
  /** What it's called: a tag's first part (`area`), a plain tag (`favorite`), or a field's key. */
  name: string
  /** The name as words: "Area", "Favorite", or the field's label. */
  label: string
  source: 'tag' | 'field'
  /** The tag itself, or the field's key. */
  ref: string
  /** For a tag: what comes after the name (`honolulu/kaimuki`; "" for a plain tag). */
  path: string
  /** The value as shown: "Honolulu / Kaimuki", "(808) 555-0123", "$18" ("" for a plain tag). */
  value: string
  kind: ValueKind
  /** For a field: its type. */
  type?: FieldType
  /** Worked out (SPEC §7.12): how, and its answer ("—" with none). */
  calc?: CalcInfo
}

/** The names everyone reaches for, offered in every + list (as fields: their values keep their spelling). */
export const COMMON_DETAILS: Array<{ key: string; type: FieldType; label: string }> = [
  { key: 'phone', type: 'text', label: 'Phone' },
  { key: 'address', type: 'place', label: 'Address' },
  { key: 'url', type: 'url', label: 'Link' },
  { key: 'amount', type: 'money', label: 'Cost' },
  // "39⅜ × 23⅝ in": what a diagram's shape hooked to the item takes as its size (SPEC §7.11).
  { key: 'size', type: 'text', label: 'Size' },
]

const PHONE = /^\+?[\d\s().-]{7,}$/
const DIGITS7 = /(\d.*){7,}/
const ADDRESS = /^\d+[a-z]?\s+\S.*\b(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|hwy|highway|pl|place|ct|court|pkwy|parkway|cir|circle|ter|terrace|loop|sq|square)\b/i

/** What a value is, from how it looks. */
export function valueKind(v: string, type?: FieldType): ValueKind {
  if (type === 'money') return 'money'
  if (type === 'date') return 'date'
  if (type === 'place') return 'address'
  if (type === 'url') return 'link'
  const s = v.trim()
  if (/^(https?:\/\/|www\.)\S+$/i.test(s)) return 'link'
  if (/^\$\s?-?[\d,]+(\.\d{1,2})?$/.test(s)) return 'money'
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'date'
  if (PHONE.test(s) && DIGITS7.test(s)) return 'phone'
  if (ADDRESS.test(s)) return 'address'
  return 'text'
}

/** The field type a new detail's first value calls for. */
export function fieldTypeFor(v: string): FieldType {
  switch (valueKind(v)) {
    case 'link':
      return 'url'
    case 'money':
      return 'money'
    case 'date':
      return 'date'
    case 'address':
      return 'place'
    default:
      return 'text'
  }
}

/**
 * Whether a new detail's value can be a tag (words, general → specific) rather than a field:
 * nothing about it would be lost and it isn't a phone, link, cost, date or address. Tags are
 * lowercase, so a capital that starts a word is fine ("North Shore" → north-shore) but one
 * inside a word isn't ("OT-48213", "iPhone").
 */
export function fitsTag(v: string): boolean {
  const s = v.trim()
  if (!s) return true
  if (valueKind(s) !== 'text') return false
  if (/[A-Z]/.test(s.replace(/(^|[\s/_.-])[A-Z]/g, '$1'))) return false
  return normalizeTag(s) === s.toLowerCase().replace(/\s+/g, '-')
}

/** A long value made short to show on one line (the whole of it is a tap away). Links lose their scheme and "www.". */
export function shortValue(v: string, max = 28): string {
  let s = v.trim()
  if (valueKind(s) === 'link')
    s = s
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/$/, '')
  return s.length > max ? s.slice(0, max - 1).trimEnd() + '…' : s
}

/** A phone number to call (`tel:`), digits and a leading + only. */
export function telHref(v: string): string {
  return 'tel:' + v.replace(/[^\d+]/g, '')
}

/** A link to open: "www.x.com" gets its https://. */
export function linkHref(v: string): string {
  const s = v.trim()
  return /^https?:\/\//i.test(s) ? s : `https://${s}`
}

/** An item's details: its tags, then its fields with values (registry order). */
export function itemDetails(item: ItemSnapshot, registry: Map<string, FieldDef>, labelOf?: (key: string, fallback: string) => string): Detail[] {
  const out: Detail[] = []
  for (const t of item.tags) {
    const g = tagGroupOf(t)
    const name = g ?? t
    const path = g ? t.slice(g.length + 1) : ''
    out.push({ name, label: tagLabel(name), source: 'tag', ref: t, path, value: path ? tagLabel(path) : '', kind: 'text' })
  }
  for (const [key, def] of registry) {
    const v = item.props[key]
    const calc = item.calc?.[key]
    if (!hasValue(v) && !calc) continue
    const value = calc ? calc.text : formatFieldValue(def, v)
    const d: Detail = { name: key, label: labelOf?.(key, def.label) ?? def.label, source: 'field', ref: key, path: '', value, kind: calc ? 'text' : valueKind(value, def.type), type: def.type }
    if (calc) d.calc = calc
    out.push(d)
  }
  return out
}

/** Where a detail shows when nobody has said: an item's own values in the grid, tags on the quick line. */
export function defaultPlace(d: Pick<Detail, 'source' | 'type'>): DetailPlace {
  return d.source === 'field' && d.type !== 'select' ? 'grid' : 'line'
}

/** Where a detail shows: as its name is set (`placement`, the household's), else its default. */
export function detailPlace(d: Pick<Detail, 'name' | 'source' | 'type'>, placement: Readonly<Record<string, DetailPlace>> | undefined): DetailPlace {
  return placement?.[d.name] ?? defaultPlace(d)
}

/** Every detail name in use (tags' first parts, plain tags, fields with values), with how many items have it, most first. */
export function detailNames(items: Iterable<ItemSnapshot>, registry: Map<string, FieldDef>): Array<[string, number]> {
  const counts = new Map<string, number>()
  for (const it of items) {
    const seen = new Set<string>()
    for (const t of it.tags) seen.add(tagGroupOf(t) ?? t)
    for (const [k, v] of Object.entries(it.props)) if (registry.has(k) && hasValue(v)) seen.add(k)
    for (const n of seen) counts.set(n, (counts.get(n) ?? 0) + 1)
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
}

// ---------------------------------------------------------------------------
// A detail as one string (how the agent reads and writes them)

/** `area/honolulu`, `favorite`, or a field as `key/value`: `phone/(808) 555-0123`. */
export function detailString(d: Detail): string {
  return d.source === 'tag' ? d.ref : `${d.name}/${d.value}`
}

export type ParsedDetail = { tag: string } | { field: FieldDef; value: string; isNew: boolean }

const loose = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
/** Loosely, and a plural is the same name ("Phones" is phone). */
const near = (s: string) => loose(s).replace(/s$/, '')

export type NamedDetail = { field: FieldDef; isNew: boolean } | { tag: string } | null

/**
 * What a detail name means, the same in the app (picking or typing one) and for the assistant:
 * a field in use (its key), one of the common ones (Phone, Address, Link, Cost), a tag in use
 * (a group like `area`, or a plain one like `favorite`; `tags`: those in use), a field in use
 * under another spelling (case, punctuation, a plural), else nothing yet (null).
 */
export function detailNamed(name: string, registry: Map<string, FieldDef>, tags: Iterable<string> = []): NamedDetail {
  const raw = name.trim().replace(/^#/, '')
  if (!raw) return null
  const exact = registry.get(raw)
  if (exact) return { field: exact, isNew: false }
  const common = COMMON_DETAILS.find((c) => loose(c.key) === loose(raw) || loose(c.label) === loose(raw))
  if (common) return registry.get(common.key) ? { field: registry.get(common.key)!, isNew: false } : { field: { ...common }, isNew: true }
  const tag = normalizeTag(raw)
  if (tag) for (const t of tags) if (isUnderTag(t, tag)) return { tag }
  const similar = [...registry.values()].find((d) => near(d.key) === near(raw) || near(d.label) === near(raw))
  return similar ? { field: similar, isNew: false } : null
}

/** The field a name means (see detailNamed): one in use, one of the common ones, or another spelling of one in use. */
export function fieldNamed(name: string, registry: Map<string, FieldDef>): { def: FieldDef; isNew: boolean } | null {
  const d = detailNamed(name, registry)
  return d && 'field' in d ? { def: d.field, isNew: d.isNew } : null
}

/**
 * A detail string read the way the app decides when one is added: a field it names keeps the
 * value as typed; words that fit a tag become a tag (general → specific); anything else (a
 * code, a phone, a link…) becomes a new field. `favorite` alone is a plain tag.
 */
export function parseDetail(s: string, registry: Map<string, FieldDef>, tags: Iterable<string> = []): ParsedDetail | null {
  const raw = s.trim().replace(/^#/, '')
  if (!raw) return null
  const i = raw.indexOf('/')
  const name = (i < 0 ? raw : raw.slice(0, i)).trim()
  const value = i < 0 ? '' : raw.slice(i + 1).trim()
  const d = detailNamed(name, registry, tags)
  if (d && 'field' in d) return { field: d.field, value, isNew: d.isNew }
  // A tag group in use (`area`): what comes after is a place in it, however it's written.
  if ((d && 'tag' in d) || fitsTag(value)) {
    const tag = normalizeTag(value ? `${name}/${value}` : name)
    return tag ? { tag } : null
  }
  const def: FieldDef = { key: fieldKeyFromLabel(name, registry.keys()), type: fieldTypeFor(value), label: name }
  return { field: def, value, isNew: true }
}
